import { useReducer } from 'react'
import styled from 'styled-components'
import { ifProp } from 'styled-tools'
import { Collapse, Grid, IconButton, Paper, Typography } from '@mui/material'
import { Duration } from 'luxon'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CorrectAnswerIcon from '@mui/icons-material/CheckCircleOutline'
import IncorrectAnswerIcon from '@mui/icons-material/HighlightOff'
import TimeLimitIcon from '@mui/icons-material/AccessTime'

import { CertificateQuestion } from 'src/types/CertificateQuestion'
import { renderQuestionAnswers } from './answers'
import { Icon } from 'src/components/CertificateQuiz/Icon'
import { spacing } from 'src/utils/styled'

const QuestionAnsweredCorrectlyIconWrapper = styled.div`
    position: absolute;
    top: ${spacing(3)};
    right: 0;
`

const ExpandIcon = styled(ExpandMoreIcon)<{ $isExpanded: boolean }>`
    transform: ${ifProp('$isExpanded', 'rotate(180deg)', 'rotate(0deg)')};
`

type CertificateQuestionDetailsProps = {
    question: CertificateQuestion
}

export function CertificateQuestionDetails({ question }: CertificateQuestionDetailsProps) {
    const [isExpanded, toggleExpanded] = useReducer((open) => !open, false)

    const hasAnsweredCorrectly = question.points === question.receivedPoints

    const duration = Duration.fromMillis(question.timeUsedMs).toFormat('mm:ss')

    if (!question.answered) {
        return <></>
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} display="flex" alignItems="center" position="relative">
                    <IconButton onClick={() => toggleExpanded()}>
                        <ExpandIcon $isExpanded={isExpanded} />
                    </IconButton>
                    <Typography variant="h5" ml={2} mr={4}>
                        {question.questionText}
                    </Typography>
                    <QuestionAnsweredCorrectlyIconWrapper>
                        <Icon tooltip={hasAnsweredCorrectly ? 'Frage wurde richtig beantwortet' : 'Frage wurde nicht richtig beantwortet'}>
                            {getIcon(hasAnsweredCorrectly)}
                        </Icon>
                    </QuestionAnsweredCorrectlyIconWrapper>
                </Grid>
                <Collapse in={isExpanded} sx={{ width: '100%' }}>
                    <Grid item xs={2} sx={{ m: 2, ml: 3, display: 'flex', alignItems: 'center' }}>
                        <Icon tooltip="Benötigte Zeit">
                            <TimeLimitIcon />
                        </Icon>
                        {duration} mins
                    </Grid>
                    <Grid item xs={12} sx={{ margin: 2 }}>
                        {renderQuestionAnswers(question)}
                    </Grid>
                </Collapse>
            </Grid>
        </Paper>
    )
}

function getIcon(hasAnsweredCorrectly: boolean) {
    if (hasAnsweredCorrectly) {
        return <CorrectAnswerIcon color="success" />
    }
    return <IncorrectAnswerIcon color="error" />
}
